import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Slider,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Grid,
  IconButton,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  ArrowLeft,
  ArrowRight,
  Brain,
  Heart,
  Moon,
  Dumbbell,
  Apple,
  Droplets,
  Smile,
  Target,
  CheckCircle,
  Calendar,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { SurveyData, SurveyQuestion } from '../types';

interface DailySurveyProps {
  onSubmit: (data: SurveyData) => void;
  lastSubmission: string | null;
}

const DailySurvey: React.FC<DailySurveyProps> = ({ onSubmit, lastSubmission }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isRetroactive, setIsRetroactive] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [formData, setFormData] = useState<Partial<SurveyData>>({
    stressLevel: 50,
    energyLevel: 50,
    sleepQuality: 50,
    physicalActivity: 50,
    nutritionScore: 50,
    hydrationLevel: 50,
    moodScore: 50,
    focusLevel: 50,
    supplements: [],
    exerciseType: '',
    sleepHours: 8,
    waterIntake: 2,
    notes: '',
  });

  const steps = [
    'Health Metrics',
    'Physical Activity',
    'Nutrition & Hydration',
    'Biohacking',
    'Review & Submit'
  ];

  const healthQuestions: SurveyQuestion[] = [
    {
      id: 'stressLevel',
      question: 'How stressed do you feel today?',
      type: 'slider',
      min: 0,
      max: 100,
      step: 1,
      category: 'health',
    },
    {
      id: 'energyLevel',
      question: 'What is your energy level today?',
      type: 'slider',
      min: 0,
      max: 100,
      step: 1,
      category: 'health',
    },
    {
      id: 'moodScore',
      question: 'How would you rate your mood today?',
      type: 'slider',
      min: 0,
      max: 100,
      step: 1,
      category: 'health',
    },
    {
      id: 'focusLevel',
      question: 'How focused do you feel today?',
      type: 'slider',
      min: 0,
      max: 100,
      step: 1,
      category: 'health',
    },
  ];

  const activityQuestions: SurveyQuestion[] = [
    {
      id: 'physicalActivity',
      question: 'How active were you today?',
      type: 'slider',
      min: 0,
      max: 100,
      step: 1,
      category: 'activity',
    },
    {
      id: 'exerciseType',
      question: 'What type of exercise did you do?',
      type: 'multiple-choice',
      options: ['None', 'Cardio', 'Strength Training', 'Yoga', 'Swimming', 'Running', 'Cycling', 'Other'],
      category: 'activity',
    },
  ];

  const nutritionQuestions: SurveyQuestion[] = [
    {
      id: 'nutritionScore',
      question: 'How well did you eat today?',
      type: 'slider',
      min: 0,
      max: 100,
      step: 1,
      category: 'nutrition',
    },
    {
      id: 'hydrationLevel',
      question: 'How well hydrated do you feel?',
      type: 'slider',
      min: 0,
      max: 100,
      step: 1,
      category: 'nutrition',
    },
    {
      id: 'waterIntake',
      question: 'How many liters of water did you drink?',
      type: 'number',
      min: 0,
      max: 10,
      step: 0.5,
      unit: 'liters',
      category: 'nutrition',
    },
  ];

  const biohackingQuestions: SurveyQuestion[] = [
    {
      id: 'sleepQuality',
      question: 'How was your sleep quality last night?',
      type: 'slider',
      min: 0,
      max: 100,
      step: 1,
      category: 'biohacking',
    },
    {
      id: 'sleepHours',
      question: 'How many hours did you sleep?',
      type: 'number',
      min: 0,
      max: 12,
      step: 0.5,
      unit: 'hours',
      category: 'biohacking',
    },
    {
      id: 'supplements',
      question: 'What supplements did you take today?',
      type: 'multiple-choice',
      options: ['Multivitamin', 'Omega-3', 'Vitamin D', 'Magnesium', 'Probiotics', 'Creatine', 'None'],
      category: 'biohacking',
    },
  ];

  const allQuestions = [
    ...healthQuestions,
    ...activityQuestions,
    ...nutritionQuestions,
    ...biohackingQuestions,
  ];

  const getCurrentQuestions = () => {
    switch (currentStep) {
      case 0: return healthQuestions;
      case 1: return activityQuestions;
      case 2: return nutritionQuestions;
      case 3: return biohackingQuestions;
      default: return [];
    }
  };

  const handleSliderChange = (questionId: string, value: number) => {
    setFormData(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSelectChange = (questionId: string, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNumberChange = (questionId: string, value: number) => {
    setFormData(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    const surveyData: SurveyData = {
      id: Date.now().toString(),
      date: isRetroactive ? selectedDate : new Date().toISOString().split('T')[0],
      stressLevel: formData.stressLevel || 50,
      energyLevel: formData.energyLevel || 50,
      sleepQuality: formData.sleepQuality || 50,
      physicalActivity: formData.physicalActivity || 50,
      nutritionScore: formData.nutritionScore || 50,
      hydrationLevel: formData.hydrationLevel || 50,
      moodScore: formData.moodScore || 50,
      focusLevel: formData.focusLevel || 50,
      supplements: formData.supplements || [],
      exerciseType: formData.exerciseType || '',
      sleepHours: formData.sleepHours || 8,
      waterIntake: formData.waterIntake || 2,
      notes: formData.notes || '',
    };

    onSubmit(surveyData);
    navigate('/');
  };

  const renderQuestion = (question: SurveyQuestion) => {
    const value = formData[question.id as keyof SurveyData];

    switch (question.type) {
      case 'slider':
        return (
          <Box sx={{ px: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {question.question}
            </Typography>
            <Slider
              value={value as number || 50}
              onChange={(_, newValue) => handleSliderChange(question.id, newValue as number)}
              min={question.min || 0}
              max={question.max || 100}
              step={question.step || 1}
              sx={{
                color: '#00ff88',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#00ff88',
                  boxShadow: '0 0 20px rgba(0, 255, 136, 0.5)',
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {question.min || 0}
              </Typography>
              <Typography variant="h6" sx={{ color: '#00ff88', fontWeight: 600 }}>
                {value || 50}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {question.max || 100}
              </Typography>
            </Box>
          </Box>
        );

      case 'multiple-choice':
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {question.question}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {question.options?.map((option) => (
                <Chip
                  key={option}
                  label={option}
                  onClick={() => {
                    if (question.id === 'supplements') {
                      const currentSupplements = formData.supplements || [];
                      const newSupplements = currentSupplements.includes(option)
                        ? currentSupplements.filter(s => s !== option)
                        : [...currentSupplements, option];
                      handleSelectChange(question.id, newSupplements);
                    } else {
                      handleSelectChange(question.id, option);
                    }
                  }}
                  variant={
                    question.id === 'supplements'
                      ? (formData.supplements || []).includes(option) ? 'filled' : 'outlined'
                      : value === option ? 'filled' : 'outlined'
                  }
                  sx={{
                    backgroundColor: value === option || (question.id === 'supplements' && (formData.supplements || []).includes(option))
                      ? '#00ff88'
                      : 'transparent',
                    color: value === option || (question.id === 'supplements' && (formData.supplements || []).includes(option))
                      ? '#000'
                      : '#fff',
                    borderColor: '#00ff88',
                    '&:hover': {
                      backgroundColor: '#00ff88',
                      color: '#000',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        );

      case 'number':
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {question.question}
            </Typography>
            <TextField
              type="number"
              value={value || ''}
              onChange={(e) => handleNumberChange(question.id, parseFloat(e.target.value) || 0)}
              inputProps={{
                min: question.min,
                max: question.max,
                step: question.step,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: '#00ff88',
                  },
                  '&:hover fieldset': {
                    borderColor: '#00ff88',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#00ff88',
                  },
                },
              }}
            />
            {question.unit && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {question.unit}
              </Typography>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  const renderReviewStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: '#00ff88', mb: 3 }}>
        Review Your Responses
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ color: '#00ff88', mb: 2 }}>
                Health Metrics
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Stress Level:</Typography>
                  <Typography variant="body2" sx={{ color: '#00ff88' }}>
                    {formData.stressLevel || 50}%
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Energy Level:</Typography>
                  <Typography variant="body2" sx={{ color: '#00ff88' }}>
                    {formData.energyLevel || 50}%
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Mood Score:</Typography>
                  <Typography variant="body2" sx={{ color: '#00ff88' }}>
                    {formData.moodScore || 50}%
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Focus Level:</Typography>
                  <Typography variant="body2" sx={{ color: '#00ff88' }}>
                    {formData.focusLevel || 50}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ color: '#00ff88', mb: 2 }}>
                Activity & Nutrition
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Physical Activity:</Typography>
                  <Typography variant="body2" sx={{ color: '#00ff88' }}>
                    {formData.physicalActivity || 50}%
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Exercise Type:</Typography>
                  <Typography variant="body2" sx={{ color: '#00ff88' }}>
                    {formData.exerciseType || 'None'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Nutrition Score:</Typography>
                  <Typography variant="body2" sx={{ color: '#00ff88' }}>
                    {formData.nutritionScore || 50}%
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Water Intake:</Typography>
                  <Typography variant="body2" sx={{ color: '#00ff88' }}>
                    {formData.waterIntake || 2}L
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ color: '#00ff88', mb: 2 }}>
                Sleep & Biohacking
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Sleep Quality:</Typography>
                  <Typography variant="body2" sx={{ color: '#00ff88' }}>
                    {formData.sleepQuality || 50}%
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Sleep Hours:</Typography>
                  <Typography variant="body2" sx={{ color: '#00ff88' }}>
                    {formData.sleepHours || 8}h
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Supplements:</Typography>
                  <Typography variant="body2" sx={{ color: '#00ff88' }}>
                    {(formData.supplements || []).length > 0 
                      ? (formData.supplements || []).join(', ')
                      : 'None'
                    }
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TextField
        fullWidth
        multiline
        rows={3}
        label="Additional Notes (Optional)"
        value={formData.notes || ''}
        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
        sx={{
          mt: 3,
          '& .MuiOutlinedInput-root': {
            color: '#fff',
            '& fieldset': {
              borderColor: '#00ff88',
            },
            '&:hover fieldset': {
              borderColor: '#00ff88',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00ff88',
            },
          },
        }}
      />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)' }}>
      <AppBar position="static" sx={{ background: 'rgba(26, 26, 26, 0.95)', backdropFilter: 'blur(10px)' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => window.history.back()}>
            <ArrowLeft />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Brain size={32} color="#00ff88" />
            <Typography variant="h6" sx={{ ml: 2, fontWeight: 700 }}>
              PeakState Daily Survey
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              icon={<Calendar />}
              label={isRetroactive ? "Retroactive Entry" : "Today's Entry"}
              onClick={() => setIsRetroactive(!isRetroactive)}
              sx={{
                backgroundColor: isRetroactive ? '#ffd700' : '#00ff88',
                color: '#000',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            />
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper sx={{ p: 4, background: 'rgba(26, 26, 26, 0.8)', backdropFilter: 'blur(10px)' }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', color: '#00ff88' }}>
                Track Your PeakState Journey
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
                Complete your daily health assessment to optimize your performance
              </Typography>
              
              {isRetroactive && (
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <TextField
                    type="date"
                    label="Select Date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': {
                          borderColor: '#ffd700',
                        },
                        '&:hover fieldset': {
                          borderColor: '#ffd700',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ffd700',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#ffd700',
                      },
                    }}
                  />
                </Box>
              )}
              
              {lastSubmission && (
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Chip
                    icon={<CheckCircle size={16} />}
                    label={`Last submission: ${new Date(lastSubmission).toLocaleDateString()}`}
                    sx={{ backgroundColor: '#00ff88', color: '#000' }}
                  />
                </Box>
              )}

              <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel sx={{ color: '#fff' }}>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            <Box sx={{ minHeight: '400px', mb: 4 }}>
              {currentStep === steps.length - 1 ? (
                renderReviewStep()
              ) : (
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ mb: 3, color: '#00ff88' }}>
                    {steps[currentStep]}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {getCurrentQuestions().map((question) => (
                      <Card key={question.id} sx={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                        <CardContent>
                          {renderQuestion(question)}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                onClick={handleBack}
                disabled={currentStep === 0}
                startIcon={<ArrowLeft />}
                sx={{
                  color: '#00ff88',
                  borderColor: '#00ff88',
                  '&:hover': {
                    borderColor: '#00ff88',
                    backgroundColor: 'rgba(0, 255, 136, 0.1)',
                  },
                }}
                variant="outlined"
              >
                Back
              </Button>

              {currentStep === steps.length - 1 ? (
                <Button
                  onClick={handleSubmit}
                  endIcon={<CheckCircle />}
                  sx={{
                    backgroundColor: '#00ff88',
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#00ff88',
                      opacity: 0.9,
                    },
                  }}
                  variant="contained"
                >
                  Submit Survey
                </Button>
              ) : (
              <Button
                onClick={handleNext}
                endIcon={<ArrowRight />}
                  sx={{
                    backgroundColor: '#00ff88',
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#00ff88',
                      opacity: 0.9,
                    },
                  }}
                  variant="contained"
                >
                  Next
                </Button>
              )}
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default DailySurvey;
